import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { reisenApi, itemsApi, kategorienApi } from '../api';

export default function ReiseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reise, setReise] = useState(null);
  const [items, setItems] = useState([]);
  const [masterItems, setMasterItems] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [showMaster, setShowMaster] = useState(false);
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [selectedMaster, setSelectedMaster] = useState([]);
  const [customItem, setCustomItem] = useState({ name: '', kategorie_id: '', menge: '', einheit: '' });
  const [filterKat, setFilterKat] = useState('alle');
  const [editingItem, setEditingItem] = useState(null);
  const [editMenge, setEditMenge] = useState('');
  const [editEinheit, setEditEinheit] = useState('');

  const load = async () => {
    const [reisenRes, itemsRes, masterRes, katRes] = await Promise.all([
      reisenApi.list(),
      reisenApi.getItems(id),
      itemsApi.list(),
      kategorienApi.list(),
    ]);
    setReise(reisenRes.data.find(r => r.id === id));
    setItems(itemsRes.data);
    setMasterItems(masterRes.data);
    setKategorien(katRes.data);
  };

  useEffect(() => { load(); }, [id]);

  const saveMenge = async (item) => {
    await reisenApi.updateItem(id, item.id, { ...item, menge: editMenge, einheit: editEinheit });
    setEditingItem(null);
    load();
  };

  const toggleItem = async (item) => {
    await reisenApi.updateItem(id, item.id, { ...item, erledigt: !item.erledigt });
    load();
  };

  const deleteItem = async (itemId) => {
    await reisenApi.deleteItem(id, itemId);
    load();
  };

  const handleAddFromMaster = async () => {
    if (!selectedMaster.length) return;
    await reisenApi.addFromMaster(id, selectedMaster);
    setSelectedMaster([]);
    setShowMaster(false);
    load();
  };

  const handleAlleHinzufuegen = async () => {
    if (!window.confirm('Alle Master-Items zur Reise hinzufügen?')) return;
    await reisenApi.alleItems(id);
    load();
  };

  const handleAddCustom = async (e) => {
    e.preventDefault();
    await reisenApi.addItems(id, [customItem]);
    setCustomItem({ name: '', kategorie_id: '' });
    setShowAddCustom(false);
    load();
  };

  const handleStatusChange = async (status) => {
    await reisenApi.update(id, { ...reise, status });
    load();
  };

  const alleErledigt = items.length > 0 && items.every(i => i.erledigt);
  const erledigtCount = items.filter(i => i.erledigt).length;

  // Gruppieren nach Kategorie
  const gruppiertItems = {};
  const gefilterteItems = filterKat === 'alle' ? items : items.filter(i => i.kategorie_id === filterKat);
  for (const item of gefilterteItems) {
    const key = item.kategorie_name || 'Sonstiges';
    const icon = item.kategorie_icon || '📦';
    if (!gruppiertItems[key]) gruppiertItems[key] = { icon, items: [] };
    gruppiertItems[key].items.push(item);
  }

  // Master Items die noch nicht in der Reise sind
  const nichtHinzugefuegt = masterItems.filter(m => !items.find(i => i.item_id === m.id));

  if (!reise) return <div style={{ padding: 20, color: '#64748b', textAlign: 'center' }}>⏳ Laden...</div>;

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', padding: 16 }}>
      <div style={{ maxWidth: 600, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: '#0ea5e9', cursor: 'pointer', fontSize: 14 }}>← Zurück</button>
          <div style={{ flex: 1 }}>
            <h1 style={{ color: '#f1f5f9', fontSize: 18, fontWeight: 700 }}>✈️ {reise.name}</h1>
            {reise.ziel && <p style={{ color: '#64748b', fontSize: 12 }}>📍 {reise.ziel}</p>}
          </div>
          <select value={reise.status} onChange={e => handleStatusChange(e.target.value)}
            style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', padding: '6px 10px', borderRadius: 8, fontSize: 12 }}>
            <option value="geplant">📅 Geplant</option>
            <option value="aktiv">✈️ Aktiv</option>
            <option value="abgeschlossen">✅ Abgeschlossen</option>
          </select>
        </div>

        {/* Fortschritt */}
        {items.length > 0 && (
          <div style={{ background: '#1e293b', borderRadius: 12, padding: 14, marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ color: '#94a3b8', fontSize: 13 }}>Fortschritt</span>
              <span style={{ color: alleErledigt ? '#34d399' : '#f1f5f9', fontWeight: 700, fontSize: 13 }}>
                {erledigtCount} / {items.length} {alleErledigt ? '🎉 Fertig!' : ''}
              </span>
            </div>
            <div style={{ background: '#0f172a', borderRadius: 4, height: 8 }}>
              <div style={{ background: alleErledigt ? '#34d399' : '#0ea5e9', height: 8, borderRadius: 4, width: `${items.length ? (erledigtCount / items.length) * 100 : 0}%`, transition: 'width 0.3s' }} />
            </div>
          </div>
        )}

        {/* Aktionsbuttons */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <button onClick={() => setShowMaster(!showMaster)} style={actionBtn('#0ea5e9')}>
            📋 Aus Master wählen
          </button>
          <button onClick={() => setShowAddCustom(!showAddCustom)} style={actionBtn('#10b981')}>
            ➕ Eigenes Item
          </button>
          <button onClick={handleAlleHinzufuegen} style={actionBtn('#f59e0b')}>
            ⚡ Alle hinzufügen
          </button>
        </div>

        {/* Master Auswahl */}
        {showMaster && (
          <div style={{ background: '#1e293b', borderRadius: 12, padding: 14, marginBottom: 14 }}>
            <h3 style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', marginBottom: 10 }}>
              Master-Liste ({nichtHinzugefuegt.length} verfügbar)
            </h3>
            <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {nichtHinzugefuegt.map(item => (
                <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: selectedMaster.includes(item.id) ? '#0ea5e922' : '#0f172a', borderRadius: 8, cursor: 'pointer', border: selectedMaster.includes(item.id) ? '1px solid #0ea5e944' : '1px solid transparent' }}>
                  <input type="checkbox" checked={selectedMaster.includes(item.id)}
                    onChange={e => setSelectedMaster(prev => e.target.checked ? [...prev, item.id] : prev.filter(i => i !== item.id))} />
                  <span style={{ fontSize: 14 }}>{item.kategorie_icon} {item.name}</span>
                </label>
              ))}
              {nichtHinzugefuegt.length === 0 && <p style={{ color: '#64748b', fontSize: 13 }}>Alle Master-Items bereits hinzugefügt!</p>}
            </div>
            {selectedMaster.length > 0 && (
              <button onClick={handleAddFromMaster} style={{ background: '#0ea5e9', color: 'white', border: 'none', padding: '10px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', width: '100%', marginTop: 10 }}>
                ✅ {selectedMaster.length} Item(s) hinzufügen
              </button>
            )}
          </div>
        )}

        {/* Eigenes Item */}
        {showAddCustom && (
          <form onSubmit={handleAddCustom} style={{ background: '#1e293b', borderRadius: 12, padding: 14, marginBottom: 14 }}>
            <h3 style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', marginBottom: 10 }}>➕ Eigenes Item</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={customItem.name} onChange={e => setCustomItem(p => ({ ...p, name: e.target.value }))}
                placeholder="Item Name *" required style={{ ...inputStyle, flex: 1 }} />
              <select value={customItem.kategorie_id} onChange={e => setCustomItem(p => ({ ...p, kategorie_id: e.target.value }))} style={{ ...inputStyle, width: 'auto' }}>
                <option value="">Kategorie</option>
                {kategorien.map(k => <option key={k.id} value={k.id}>{k.icon} {k.name}</option>)}
              </select>
            </div>
            <button type="submit" style={{ background: '#10b981', color: 'white', border: 'none', padding: '10px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', width: '100%', marginTop: 8 }}>
              ➕ Hinzufügen
            </button>
          </form>
        )}

        {/* Filter */}
        {kategorien.length > 0 && items.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
            <button onClick={() => setFilterKat('alle')} style={filterBtn(filterKat === 'alle')}>Alle ({items.length})</button>
            {kategorien.filter(k => items.find(i => i.kategorie_id === k.id)).map(k => (
              <button key={k.id} onClick={() => setFilterKat(k.id)} style={filterBtn(filterKat === k.id)}>
                {k.icon} {k.name} ({items.filter(i => i.kategorie_id === k.id).length})
              </button>
            ))}
          </div>
        )}

        {/* Items gruppiert */}
        {items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🧳</div>
            <p>Noch keine Items</p>
            <p style={{ fontSize: 13, marginTop: 8 }}>Füge Items aus der Master-Liste hinzu</p>
          </div>
        ) : (
          Object.entries(gruppiertItems).map(([kat, data]) => (
            <div key={kat} style={{ marginBottom: 16 }}>
              <h3 style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                {data.icon} {kat} ({data.items.filter(i => i.erledigt).length}/{data.items.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {data.items.map(item => (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#1e293b', borderRadius: 10, padding: '12px 14px', border: item.erledigt ? '1px solid #10b98122' : '1px solid transparent' }}>
                    <button onClick={() => toggleItem(item)} style={{ background: item.erledigt ? '#10b981' : '#334155', border: 'none', borderRadius: '50%', width: 26, height: 26, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {item.erledigt ? '✓' : ''}
                    </button>
                    <div style={{ flex: 1 }}>
                      <span style={{ color: item.erledigt ? '#64748b' : '#f1f5f9', fontSize: 14, textDecoration: item.erledigt ? 'line-through' : 'none' }}>
                        {item.name}
                      </span>
                      {editingItem === item.id ? (
                        <div style={{ display: 'flex', gap: 4, marginTop: 6 }} onClick={e => e.stopPropagation()}>
                          <input value={editMenge} onChange={e => setEditMenge(e.target.value)}
                            placeholder="Menge" style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', padding: '4px 8px', borderRadius: 6, fontSize: 12, width: 70, outline: 'none' }} />
                          <input value={editEinheit} onChange={e => setEditEinheit(e.target.value)}
                            placeholder="Einheit" style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', padding: '4px 8px', borderRadius: 6, fontSize: 12, width: 80, outline: 'none' }} />
                          <button onClick={() => saveMenge(item)} style={{ background: '#10b981', color: 'white', border: 'none', padding: '4px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>✓</button>
                          <button onClick={() => setEditingItem(null)} style={{ background: '#334155', color: '#94a3b8', border: 'none', padding: '4px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>✕</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                          {item.menge && <span style={{ color: '#0ea5e9', fontSize: 12, fontWeight: 600 }}>{item.menge}{item.einheit ? ' ' + item.einheit : ''}</span>}
                          <button onClick={e => { e.stopPropagation(); setEditingItem(item.id); setEditMenge(item.menge || ''); setEditEinheit(item.einheit || ''); }}
                            style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 11, padding: '0 2px' }}>✏️</button>
                        </div>
                      )}
                    </div>
                    <button onClick={() => deleteItem(item.id)} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 16, padding: '2px 6px' }}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}

        {/* Alle zurücksetzen */}
        {items.length > 0 && erledigtCount > 0 && (
          <button onClick={async () => {
            for (const item of items) {
              if (item.erledigt) await reisenApi.updateItem(id, item.id, { ...item, erledigt: false });
            }
            load();
          }} style={{ background: '#1e293b', color: '#64748b', border: '1px solid #334155', padding: '10px', borderRadius: 8, cursor: 'pointer', fontSize: 13, width: '100%', marginTop: 8 }}>
            🔄 Alle zurücksetzen
          </button>
        )}
      </div>
    </div>
  );
}
const inputStyle = { background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', padding: '10px 12px', borderRadius: 8, fontSize: 14, outline: 'none', width: '100%' };
const actionBtn = (color) => ({ background: color + '22', color, border: `1px solid ${color}44`, padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 });
const filterBtn = (active) => ({ background: active ? '#0ea5e922' : '#1e293b', color: active ? '#0ea5e9' : '#64748b', border: active ? '1px solid #0ea5e944' : '1px solid #334155', padding: '4px 10px', borderRadius: 16, cursor: 'pointer', fontSize: 12 });
